import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Alert, Box, Chip, Flex, Header, Name, Tabs } from '~/components/blocks';
import { Tab } from '~/components/blocks/Tabs/Tab';
import {
  DirectVisitorDetailDialogProfileFragment,
  LineVisitorDetailDialogProfileFragment,
  PatientDetailDialogProfileFragment,
  WebVisitorDetailDialogProfileFragment,
} from '~/graphql/generated/graphql';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import {
  patientDetailConfirmBreakawayState,
  patientDetailDialogMessageState,
  patientDetailDialogState,
} from '~/state/partials/patient_detail_dialog/atoms';
import {
  PatientDetailDiaLogActionType,
  TabType,
} from '~/state/partials/patient_detail_dialog/types';

type Props = {
  patient?:
    | DirectVisitorDetailDialogProfileFragment
    | WebVisitorDetailDialogProfileFragment
    | PatientDetailDialogProfileFragment
    | LineVisitorDetailDialogProfileFragment;
  statusManagement: boolean;
};

const PatientName = styled(Flex)<{ isPharmacist: boolean }>(({ theme, isPharmacist }) =>
  css({
    width: isPharmacist ? '373px' : 'auto',
    height: '59px',
    justifyContent: 'start',
    alignItems: 'center',
    [`@media ${theme.mediaQueries.tablet}`]: {
      width: isPharmacist ? '272px' : 'auto',
    },
  }),
);

const CustomHeader = styled(Header)(({ theme }) =>
  css({
    borderBottom: theme.borders.default,
    height: '58px',
    paddingLeft: '48px',
  }),
);

const InfoAlert = styled(Alert)(({ theme }) =>
  css({
    height: '100%',
    textAlign: 'initial',
    margin: theme.space.l,
    padding: theme.space.l,
    fontSize: theme.fontSizes.s,
    [`@media ${theme.mediaQueries.tablet}`]: {
      fontSize: theme.fontSizes.xs,
      margin: theme.space.l,
      padding: theme.space.m,
    },
  }),
);

export const DialogHeder = React.memo((props: Props) => {
  const theme = useTheme();
  const { patient, statusManagement } = props;
  const [{ selectedMessageId }, setPatientDetailDialogMessageState] = useAtom(
    patientDetailDialogMessageState,
  );
  const [{ type }, setPatientDetailDialogState] = useAtom(patientDetailDialogState);
  const setPatientDetailConfirmBreakawayState = useSetAtom(patientDetailConfirmBreakawayState);
  const { verifyRole } = usePractitionerRole();
  const isPharmacist = verifyRole('pharmacist');

  const followupStatus = patient?.medicationFollowupStatus;

  const handleCreateTracingReport = useCallback(() => {
    if (selectedMessageId) {
      setPatientDetailConfirmBreakawayState((_state) => ({
        ..._state,
        isOpen: true,
        action: 'toTracingReport' as PatientDetailDiaLogActionType,
      }));
    } else {
      if (patient?.id) {
        setPatientDetailDialogState((_state) => ({
          ..._state,
          patientId: patient?.id,
          isOpen: true,
          type: 'tracingreport' as TabType,
        }));
        setPatientDetailDialogMessageState((_state) => ({
          ..._state,
          messageInfoStatus: 'new' as const,
          selectedMessageId: null,
        }));
      }
    }
  }, [
    patient,
    selectedMessageId,
    setPatientDetailConfirmBreakawayState,
    setPatientDetailDialogState,
    setPatientDetailDialogMessageState,
  ]);

  const handleCreateMedicationFollowupMessage = useCallback(() => {
    if (patient?.id) {
      setPatientDetailDialogMessageState((_state) => ({
        ..._state,
        selectedMessageId: null,
      }));
      setPatientDetailDialogState((_state) => ({
        ..._state,
        patientId: patient?.id,
        isOpen: true,
        type: 'message' as TabType,
      }));
    }
  }, [patient, setPatientDetailDialogMessageState, setPatientDetailDialogState]);

  if (!patient) return null;

  return (
    <CustomHeader>
      <Flex>
        <PatientName isPharmacist={isPharmacist}>
          <Name
            familyName={patient.familyName}
            givenName={patient.givenName}
            phoneticFamilyName={patient.phoneticFamilyName}
            phoneticGivenName={patient.phoneticGivenName}
          />
          {statusManagement && followupStatus === 'in_progress' && (
            <Chip size="fixed" color="blue" marginLeft={theme.space.m}>
              対応中
            </Chip>
          )}
        </PatientName>
      </Flex>
      <Flex
        height="58px"
        alignItems={`${isPharmacist ? 'flex-end' : 'center'}`}
        padding={`${!isPharmacist && theme.space.m}`}
      >
        {isPharmacist ? (
          <Box>
            <Tabs>
              <Tab active={type === 'message'} onClick={handleCreateMedicationFollowupMessage}>
                メッセージ
              </Tab>
              <Tab active={type === 'tracingreport'} onClick={handleCreateTracingReport}>
                トレーシングレポート
              </Tab>
            </Tabs>
          </Box>
        ) : (
          <InfoAlert status="info">
            服薬指導権限がないため、メッセージ作成・トレーシングレポートの作成ができません。
          </InfoAlert>
        )}
      </Flex>
    </CustomHeader>
  );
});

DialogHeder.displayName = 'DialogHeder';
